import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Trash2, Plus, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    title_fr: '',
    title_en: '',
    description_fr: '',
    description_en: '',
    category: 'huile',
    image_url: ''
  });

  useEffect(() => {
    if (activeTab === 'contacts') {
      fetchContacts();
    } else {
      fetchGalleryItems();
    }
  }, [activeTab]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
    setLoading(false);
  };

  const fetchGalleryItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gallery`);
      setGalleryItems(response.data);
    } catch (error) {
      console.error('Error fetching gallery items:', error);
    }
    setLoading(false);
  };

  const deleteGalleryItem = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    
    try {
      await axios.delete(`${API}/gallery/${id}`);
      setGalleryItems(galleryItems.filter(item => item.id !== id));
      alert('Produit supprimé avec succès');
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const addGalleryItem = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/gallery`, newProduct);
      setGalleryItems([...galleryItems, response.data]);
      setShowAddModal(false);
      setNewProduct({
        title_fr: '',
        title_en: '',
        description_fr: '',
        description_en: '',
        category: 'huile',
        image_url: ''
      });
      alert('Produit ajouté avec succès');
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Erreur lors de l\'ajout');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-stone-800 mb-2" data-testid="admin-title">Backoffice Barkatis Group</h1>
          <p className="text-gray-600">Gestion des contacts et de la galerie de produits</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('contacts')}
              className={`px-6 py-3 font-semibold ${
                activeTab === 'contacts'
                  ? 'border-b-2 border-amber-600 text-amber-600'
                  : 'text-gray-600 hover:text-amber-600'
              }`}
              data-testid="tab-contacts"
            >
              Messages Contacts
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`px-6 py-3 font-semibold ${
                activeTab === 'gallery'
                  ? 'border-b-2 border-amber-600 text-amber-600'
                  : 'text-gray-600 hover:text-amber-600'
              }`}
              data-testid="tab-gallery"
            >
              Galerie Produits
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : activeTab === 'contacts' ? (
          /* Contacts Table */
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sujet</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        Aucun message pour le moment
                      </td>
                    </tr>
                  ) : (
                    contacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{contact.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.company || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.subject}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{contact.message}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(contact.created_at).toLocaleDateString('fr-FR')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Gallery Management */
          <div>
            <div className="mb-6 flex justify-end">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center space-x-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-semibold"
                data-testid="add-product-button"
              >
                <Plus size={20} />
                <span>Ajouter un produit</span>
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryItems.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-500">
                  Aucun produit dans la galerie
                </div>
              ) : (
                galleryItems.map((item) => (
                  <div key={item.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <div 
                      className="h-48 bg-cover bg-center" 
                      style={{ backgroundImage: `url(${item.image_url})` }}
                    ></div>
                    <div className="p-4">
                      <h3 className="text-lg font-bold text-stone-800 mb-2">
                        {language === 'fr' ? item.title_fr : item.title_en}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">
                        {language === 'fr' ? item.description_fr : item.description_en}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          item.category === 'huile' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {item.category === 'huile' ? 'Huile' : 'Mosaïque'}
                        </span>
                        <button
                          onClick={() => deleteGalleryItem(item.id)}
                          className="text-red-600 hover:text-red-800"
                          data-testid={`delete-product-${item.id}`}
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-stone-800">Ajouter un produit</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={addGalleryItem} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Titre (Français) *</label>
                  <input
                    type="text"
                    value={newProduct.title_fr}
                    onChange={(e) => setNewProduct({...newProduct, title_fr: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Titre (English) *</label>
                  <input
                    type="text"
                    value={newProduct.title_en}
                    onChange={(e) => setNewProduct({...newProduct, title_en: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description (Français) *</label>
                  <textarea
                    value={newProduct.description_fr}
                    onChange={(e) => setNewProduct({...newProduct, description_fr: e.target.value})}
                    required
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description (English) *</label>
                  <textarea
                    value={newProduct.description_en}
                    onChange={(e) => setNewProduct({...newProduct, description_en: e.target.value})}
                    required
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Catégorie *</label>
                  <select
                    value={newProduct.category}
                    onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="huile">Huile</option>
                    <option value="mosaique">Mosaïque</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">URL de l'image *</label>
                  <input
                    type="url"
                    value={newProduct.image_url}
                    onChange={(e) => setNewProduct({...newProduct, image_url: e.target.value})}
                    required
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                <div className="flex space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold"
                  >
                    Ajouter
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;